import { tr } from '@/lib/i18n-core';
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { Button, Card, CardContent, CardHeader, CardTitle, Checkbox } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { BranchTaxCard } from './BranchTaxCard';

interface BillingForm {
  requireTxnRef: boolean;
  requireOpenShift: boolean;
  allowCashWithoutShift: boolean;
  blindCashCount: boolean;
}

function formFrom(settings: Record<string, unknown>): BillingForm {
  return {
    requireTxnRef: settings.requireTxnRef === true,
    requireOpenShift: settings.requireOpenShift === true,
    allowCashWithoutShift: settings.allowCashWithoutShift !== false,
    blindCashCount: settings.blindCashCount === true,
  };
}

export function BillingSettingsTab({ readOnly, settings }: { readOnly: boolean; settings: Record<string, unknown> }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<BillingForm>(() => formFrom(settings));

  useEffect(() => {
    setForm(formFrom(settings));
  }, [settings]);

  const save = useMutation({
    mutationFn: () =>
      put('/org/settings', {
        requireTxnRef: form.requireTxnRef,
        requireOpenShift: form.requireOpenShift,
        allowCashWithoutShift: form.allowCashWithoutShift,
        blindCashCount: form.blindCashCount,
      }),
    onSuccess: () => {
      toast.success(tr('admin', 'billing_settings_saved'));
      void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
    },
    onError: (error) => toast.error(tr('admin', 'could_not_save_the_settings'), describeError(error)),
  });

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <BranchTaxCard settings={settings} settingsReadOnly={readOnly} />

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{tr('admin', 'payments_and_cash')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Checkbox
            checked={form.requireTxnRef}
            disabled={readOnly}
            onCheckedChange={(checked) => setForm({ ...form, requireTxnRef: checked })}
            label={<strong>{tr('admin', 'require_a_reference_for_card_transfer_wallet')}</strong>}
            description={tr('admin', 'the_slip_or_transaction_number_must_be_typed')}
          />
          <Checkbox
            checked={form.requireOpenShift}
            disabled={readOnly}
            onCheckedChange={(checked) => setForm({ ...form, requireOpenShift: checked })}
            label={<strong>{tr('admin', 'require_an_open_cashier_shift_to_sell_or_take')}</strong>}
            description={tr('admin', 'every_cash_movement_is_then_tied_to_a_drawer')}
          />
          <Checkbox
            checked={form.allowCashWithoutShift}
            disabled={readOnly}
            onCheckedChange={(checked) => setForm({ ...form, allowCashWithoutShift: checked })}
            label={<strong>{tr('admin', 'allow_cash_to_be_taken_or_paid_out_when_nobody')}</strong>}
            description={tr('admin', 'switch_off_to_refuse_cash_refunds_and_advances')}
          />
          <Checkbox
            checked={form.blindCashCount}
            disabled={readOnly}
            onCheckedChange={(checked) => setForm({ ...form, blindCashCount: checked })}
            label={<strong>{tr('admin', 'blind_cash_count_hide_the_expected_cash_from')}</strong>}
            description={tr('admin', 'supervisors_and_finance_readers_still_see_it')}
          />
          {!readOnly && (
            <Button onClick={() => save.mutate()} loading={save.isPending}>
              {tr('admin', 'save_billing_settings')}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
